<script>
    import { onMount, onDestroy } from "svelte";

    import { isMobile } from "../../../stores";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getAmountInCurrency,
        getFormatDate,
        getText,
        replaceURL,
        showError,
        showException,
        syncDefaultDoctorFilter,
    } from "../../../Utils/utilFunctions.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        SearchSelect,
        SvelteTable,
        PageLoading,
        NotificationModal,
        SearchInput,
        ResetFilterLink,
        DoctorMultiSearchSelect,
        DefaultDoctorFilter,
    } from "../../../Components/Common/index.svelte";

    import ERAsEncountersColumn from "./ERAsEncountersColumn.svelte";

    import TableFooter from "../../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};
    export let toggleShowERAsTable = () => {};

    let isLoadingReconciliationERAs = false,
        returnedReconciliationERAs = [],
        filters = { reconciled: Number(0) },
        selectedReconciliationEraID = Number(0);

    let isReconcileEmptyEFTsModalOpen = false,
        isReconciling = false,
        freeText;

    let isUndoReconcileModalOpen = false,
        isSendingUndoReconcileRequest = false,
        isShowUndoReconcileSuccessModal = false;

    let page = 0,
        pageSize = 25,
        count = 0;

    let selectedDoctors = [];

    const columns = [
        {
            key: "id",
            title: "ID",
            headerClass:
                "text-left px-3 w-[5%] cursor-default ml-3 rounded-tl-xl",
            class: "text-left px-3 w-[5%] cursor-default",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "eraCreatedDate",
            title: "ERA Created Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.eraCreatedDate),
        },
        {
            key: "encounterDetails",
            title: "Encounters",
            headerClass: "text-left px-3 w-[30%] cursor-default ml-3",
            class: "text-left px-3 w-[30%] cursor-default",
            renderComponent: {
                component: ERAsEncountersColumn,
            },
            csvContent: (v) => {
                const { encounters } = v;

                if (!encounters || !encounters.length) return "-";

                return `${encounters[0].patientEncounterId}, Provider: ${encounters[0].providerNumer}, Amount: ${getAmountInCurrency(encounters[0].amount)} ${encounters.length > 1 ? "\r\n Multiple Encounters" : ""}`;
            },
        },
        {
            key: "reconciledDate",
            title: "Reconciled Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.reconciledDate),
        },
        {
            key: "reconciledBy",
            title: "Reconciled By",
            headerClass: "text-left px-3 w-[30%] cursor-default ml-3",
            class: "text-left px-3 w-[30%] cursor-default",
            renderValue: (v) => _renderUsers(v.userDetail),
        },
        {
            key: "eraAmount",
            title: "Amount",
            headerClass: "text-left px-3 w-[5%] cursor-default ml-3",
            class: "text-left px-3 w-[5%] cursor-default",
            renderValue: (v) => getAmountInCurrency(v.eraAmount),
        },
        {
            key: "eraPayer",
            title: "Payer",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getText(v.eraPayer),
        },
        {
            key: "eraPayerDate",
            title: "Paid Date",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getFormatDate(v.eraPayerDate),
        },
        {
            key: "eraReference",
            title: "Reference",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getText(v.eraReference),
        },
        {
            key: "eraTransactionId",
            title: "Transaction ID",
            headerClass: "text-left px-3 w-[10%] cursor-default ml-3",
            class: "text-left px-3 w-[10%] cursor-default",
            renderValue: (v) => getText(v.eraTransactionId),
        },
        {
            key: "eraLogTransactionId",
            title: "Log Transaction ID",
            headerClass: "text-left px-3 w-[13%] cursor-default ml-3",
            class: "text-left px-3 w-[13%] cursor-default",
            renderValue: (v) => getText(v.eraLogTransactionId),
        },
        {
            key: "notes",
            title: "Notes",
            headerClass: "text-left px-3 w-[13%] cursor-default ml-3",
            class: "text-left px-3 w-[13%] cursor-default",
            renderValue: (v) => getText(v.notes),
        },
        {
            key: "actions",
            title: "",
            headerClass: "text-right px-3 w-[5%] cursor-default rounded-tr-xl",
            class: "text-right px-3 w-[5%] cursor-default",
            renderValue: (v) => _renderActionIcons(v.reconciled),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        filters = await syncDefaultDoctorFilter(filters, "doctorId");

        loadReconciliationERAs(page * pageSize, pageSize, _getSearchParams());
    });

    onDestroy(() => {
        selectedReconciliationEraID = Number(0);
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1];

                if (arr[i].includes("doctorId=")) {
                    if (isNaN(arr[i].split("=")[1])) {
                        filters.doctorId = arr[i].split("=")[1];
                        selectedDoctors = filters.doctorId
                            .split(",")
                            .map((id) => Number(id));
                    } else {
                        filters.doctorId = Number(arr[i].split("=")[1]);
                        selectedDoctors = [Number(filters.doctorId)];
                    }
                }

                if (arr[i].includes("amountFrom="))
                    filters.amountFrom = Number(arr[i].split("=")[1]);

                if (arr[i].includes("amountTo="))
                    filters.amountTo = Number(arr[i].split("=")[1]);

                if (arr[i].includes("reconciled="))
                    filters.reconciled = Number(arr[i].split("=")[1]);
            }
        }

        updateURL();
    };

    const loadReconciliationERAs = async (start, limit, params) => {
        isLoadingReconciliationERAs = true;

        await apiMethods.getMethod
            .getReconciliationERA(start, limit, params)
            .then((res) => _handleReconciliationERAs(res))
            .catch((err) =>
                showException(
                    "Failed to load data, please refresh the page and try again and contact support if the issue persists.",
                ),
            );

        isLoadingReconciliationERAs = false;
    };

    const confirmReconciliationEmptyEFTs = async () => {
        isReconciling = true;

        await apiMethods.putMethod
            .updateReconciliationERA({
                id: selectedReconciliationEraID,
                eftReconciliationIds: [],
                notes: freeText,
            })
            .then((res) => {
                if (res.error) showError(res.error);
                else {
                    freeText = "";
                    selectedReconciliationEraID = Number(0);

                    loadReconciliationERAs(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
                }
            })
            .catch((err) => showException(err));

        _closeReconcileEmptyEFTsModal();

        isReconciling = false;
    };

    const undoERA = async () => {
        isSendingUndoReconcileRequest = true;

        await apiMethods.putMethod
            .sendUndoEraOrEftRequest("era", selectedReconciliationEraID)
            .then((res) => _handleUndoReconcileRequest(res))
            .catch((err) => showException(err));

        _closeUndoReconcileModal();

        isSendingUndoReconcileRequest = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadReconciliationERAs(0, pageSize, _getSearchParams());
    };

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };

    const updateURL = () => {
        replaceURL(
            `/Reconciliations/tab=ERAs&page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadReconciliationERAs(page * pageSize, pageSize, _getSearchParams());
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadReconciliationERAs(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className, id } }, rowData, key) => {
        if (key == "actions") {
            selectedReconciliationEraID = Number(rowData.id);
            if (className.includes("reconciledIcon"))
                toggleShowERAsTable(selectedReconciliationEraID);
            else if (className.includes("reconcileWithoutMatchingIcon"))
                _openReconcileEmptyReceiptsModal();
            else if (className.includes("undoIcon")) _openUndoReconcileModal();
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    // HELPER FUNCTIONS
    const _handleReconciliationERAs = async (res, isCsvContent = false) => {
        // res = {
        //     start: 0,
        //     limit: 25,
        //     total: 1,
        //     results: [
        //         {
        //             id: 1,
        //             source: "string",
        //             createdDate: "2023-08-16T00:09:26.082Z",
        //             updatedDate: "2023-08-16T00:09:26.082Z",
        //             deletedDate: "2023-08-16T00:09:26.082Z",
        //             error: "",
        //             eraTransactionId: "string",
        //             eraLogTransactionId: "string",
        //             eraCreatedDate: "2023-08-16T00:09:26.082Z",
        //             eraAmount: 123,
        //             eraReference: "string",
        //             eraPayer: "string",
        //             eraPayerDate: "2023-08-16T00:09:26.082Z",
        //             reconciled: true,
        //             reconciledBy: 1,
        //             reconciledDate: "2023-08-16T00:09:26.082Z",
        //             notes: "string",
        //             eftReconciliationIds: [1],
        //             encounters: [
        //                 {
        //                     id: 1,
        //                     reconciliationERAId: 1,
        //                     patientEncounterId: 1,
        //                     providerNumer: "string",
        //                     amount: 123,
        //                 },
        //             ],
        //         },
        //     ],
        // };

        if (res.error) showError(res.error);
        else if (res.results && res.results.length) {
            let results = [];

            if (!isCsvContent) {
                returnedReconciliationERAs = [...res.results];
                count = res.total;
            }

            results = await _mapUserName(res.results);

            if (isCsvContent) return results;
            else returnedReconciliationERAs = [...results];
        } else if (!res.results.length) {
            returnedReconciliationERAs = [];
            count = Number(0);

            return [];
        }
    };

    const _handleUndoReconcileRequest = (res) => {
        if (!res || !res.id || res.error)
            showError(
                "Failed to undo selected ERA, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            _openUndoReconcileSuccessModal();
            loadReconciliationERAs(0, pageSize, _getSearchParams());
        }
    };

    const _mapUserName = async (arr) => {
        if (!arr.length) return;

        const userIdArr = [];
        const promises = [];

        arr.forEach((reconciliation) => {
            if (
                reconciliation.reconciledBy &&
                !userIdArr.includes(reconciliation.reconciledBy)
            ) {
                userIdArr.push(reconciliation.reconciledBy);

                promises.push(
                    apiMethods.getMethod
                        .getUserById(Math.abs(reconciliation.reconciledBy))
                        .then((res) => {
                            if (res.id) {
                                arr.map((reconciliationDataForAcc) => {
                                    if (
                                        reconciliationDataForAcc.reconciledBy ==
                                        res.id
                                    )
                                        reconciliationDataForAcc.userDetail = {
                                            name: res.fullName || "",
                                            email: res.email || "",
                                        };
                                });
                            }
                        }),
                );
            }
        });

        await Promise.all(promises);

        return arr;
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;

        let { amountFrom, amountTo } = filters;
        
        // If both exist and are in the wrong order, swap them
        if (amountFrom && amountTo && amountFrom > amountTo) {
            [amountFrom, amountTo] = [amountTo, amountFrom];
        }

        // Assign to params if they exist
        if (amountFrom) params["filter[amountFrom]"] = amountFrom;
        if (amountTo) params["filter[amountTo]"] = amountTo;

        if (filters.reconciled || filters.reconciled == Number(0))
            params["filter[reconciled]"] = Number(filters.reconciled);

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[doctorId]"])
                str += `&doctorId=${params["filter[doctorId]"]}`;

            if (
                params["filter[amountFrom]"] ||
                params["filter[amountFrom]"] == Number(0)
            )
                str += `&amountFrom=${params["filter[amountFrom]"]}`;

            if (
                params["filter[amountTo]"] ||
                params["filter[amountTo]"] == Number(0)
            )
                str += `&amountTo=${params["filter[amountTo]"]}`;

            if (
                params["filter[reconciled]"] ||
                params["filter[reconciled]"] == Number(0)
            )
                str += `&reconciled=${params["filter[reconciled]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _renderUsers = (userDetail) => {
        if (!userDetail) return "";

        if (!userDetail.name)
            if (!userDetail.email) return "";
            else return getText(userDetail.email);

        if (!userDetail.email) return getText(userDetail.name);

        return `${getText(userDetail.name)} (${getText(userDetail.email)})`;
    };

    const _renderActionIcons = (reconciled) => {
        return reconciled
            ? `<div class="flex flex-1 items-center justify-center gap-x-2">
                    <div class="undoIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Undo Reconcile">
                        <i class="undoIcon fa-solid fa-rotate-left cursor-pointer"></i>
                    </div>
                </div>`
            : `<div class="flex flex-1 items-center justify-center gap-x-2">
                    <div class="reconciledIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Reconcile">
                        <i class="reconciledIcon fa-solid fa-list-check cursor-pointer"></i>
                    </div>

                    <div class="reconcileWithoutMatchingIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Reconcile Without Matching EFTs">
                        <i class="reconcileWithoutMatchingIcon fa-solid fa-triangle-exclamation"></i>
                    </div>
                </div>`;
    };

    const _openReconcileEmptyReceiptsModal = () =>
        (isReconcileEmptyEFTsModalOpen = true);

    const _closeReconcileEmptyEFTsModal = () =>
        (isReconcileEmptyEFTsModalOpen = false);

    const _openUndoReconcileModal = () => (isUndoReconcileModalOpen = true);

    const _closeUndoReconcileModal = () => {
        isUndoReconcileModalOpen = false;
        selectedReconciliationEraID = Number(0);
    };

    const _openUndoReconcileSuccessModal = () =>
        (isShowUndoReconcileSuccessModal = true);

    const _closeUndoReconcileSuccessModal = () =>
        (isShowUndoReconcileSuccessModal = false);
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-col gap-y-4 flex-0">
        <form class="flex flex-row">
            <div class="flex gap-4 flex-wrap items-end">
                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                />

                <DefaultDoctorFilter
                    bind:filter={filters}
                    filterPropertyName="doctorId"
                    onChangeFunc={() => {
                        selectedDoctors = filters["doctorId"].split(",");
                        handleFilterSelect();
                    }}
                />

                <DoctorMultiSearchSelect
                    bind:searchResult={selectedDoctors}
                    onChangeFunc={handleSelectDoctor}
                    inputClass="w-32"
                    zIndex="3"
                />

                <SearchInput
                    bind:value={filters.amountFrom}
                    title="Amount From"
                    type="number"
                    inputFunc={() => {
                        if (!isNaN(filters.amountFrom)) handleFilterSelect();
                    }}
                />

                <SearchInput
                    bind:value={filters.amountTo}
                    title="Amount To"
                    type="number"
                    inputFunc={() => {
                        if (!isNaN(filters.amountTo)) handleFilterSelect();
                    }}
                />

                <SearchSelect
                    bind:value={filters.reconciled}
                    title="Reconciled"
                    onChangeFunc={handleFilterSelect}
                >
                    <option value={Number(1)}>Yes</option>
                    <option value={Number(0)}>No</option>
                </SearchSelect>

                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={() => {
                        clearFilterDoctor();
                    }}
                />
            </div>
        </form>
    </div>

    {#if isLoadingReconciliationERAs}
        <PageLoading />
    {:else if returnedReconciliationERAs.length}
        <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnedReconciliationERAs}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        </div>

        <TableFooter
            {page}
            {pageSize}
            {count}
            tableData={returnedReconciliationERAs}
            {onPageChange}
            {onPageSizeChange}
            csvConfig={{
                columns,
                data: returnedReconciliationERAs,
                fileName: "Reconciliation ERAs",
                allDataApi: async () => {
                    let res = await apiMethods.getMethod.getReconciliationERA(
                        0,
                        999999,
                        _getSearchParams(),
                    );

                    const results = await _handleReconciliationERAs(res, true);

                    return { results };
                },
            }}
        />
    {:else}
        <div class="flex flex-1 items-center justify-center text-2xl font-bold">
            No ERAs
        </div>
    {/if}
</div>

<NotificationModal
    isModalOpen={isUndoReconcileModalOpen}
    title="Undo reconciliation?"
    cancelText="Cancel"
    submitText="Confirm"
    closeModal={_closeUndoReconcileModal}
    confirmAction={undoERA}
    isConfirming={isSendingUndoReconcileRequest}
    closeModalFunction={_closeUndoReconcileModal}
>
    <p class="leading-6">
        This will mark the ERA and associated EFTs as <i>NOT reconciled</i>,
        change the status of ALL associated payments to <i>Pending</i> and
        change the billing status of associated encounters to
        <i>In Progress</i>.
    </p>
    <p class="leading-6">Continue?</p>
</NotificationModal>

<NotificationModal
    isModalOpen={isReconcileEmptyEFTsModalOpen}
    isConfirming={isReconciling}
    closeModal={_closeReconcileEmptyEFTsModal}
    confirmAction={confirmReconciliationEmptyEFTs}
    submitText={isReconciling ? "Confirming" : "Reconcile"}
    title="Reconcile this ERA without matching it to a EFT?"
    cancelText="Cancel"
    closeModalFunction={_closeReconcileEmptyEFTsModal}
>
    <textarea
        cols="30"
        rows="5"
        class="textarea w-full textarea-bordered"
        bind:value={freeText}
        placeholder="Notes"
        autocomplete="off"
    />
</NotificationModal>

<NotificationModal
    isModalOpen={isShowUndoReconcileSuccessModal}
    closeModal={_closeUndoReconcileSuccessModal}
    closeModalFunction={_closeUndoReconcileModal}
    showConfirmBtn={false}
>
    Successfully undo selected reconciliation
</NotificationModal>
